import { useAbility } from '@casl/react'
import { Section } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'
import copy from 'copy-to-clipboard'
import { last } from 'lodash'
import { values } from 'mobx'
import { observer } from 'mobx-react-lite'
import QueryString from 'qs'
import { Key, useRef } from 'react'
import { useSelector } from 'react-redux'
import { generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useModals } from 'almanac-ui/Modal'
import {
  ArrowOut,
  BlazeAI,
  Comment,
  Copy,
  Delete,
  Edit,
  Export,
  Favorite,
  Focus,
  History,
  Link,
  Login,
  Merge,
  Move,
  Print,
  ReadReceipt,
  Share,
  Unfavorite,
} from 'almanac-ui/icons'
import { FavoritableType } from 'api/clients/FavoriteClient'
import { FolderClient } from 'api/clients/FolderClient'
import { UserService } from 'api/clients/UserService'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UserAppPreferencesService } from 'api/services/UserAppPreferencesService'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useArticleViewChange } from 'components/ArticleViewSelect/useArticleViewChange'
import { useArticleViewRequestChange } from 'components/ArticleViewSelect/useArticleViewRequestChange'
import { AbilityContext, canViewIn, useCanViewAs } from 'components/Can'
import CopyModal from 'components/CopyModal/CopyModal'
import { useOnItemCopied } from 'components/CopyModal/useOnItemCopied'
import { ExportModal } from 'components/ExportModal/ExportModal'
import { Menu } from 'components/Menu/Menu'
import DeleteModal from 'components/NavRightClickMenu/components/DeleteModal/DeleteModal'
import { PublishDocModal } from 'components/PublishDocModal/PublishDocModal'
import { ShareSheet } from 'components/ShareSheet/ShareSheet'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { VisualSwitch } from 'components/VisualSwitch/VisualSwitch'
import { useDocumentationFeatures } from 'hooks/useDocumentationFeatures'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useLoggedIn } from 'hooks/useLoggedIn'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useQueryParams } from 'hooks/useQueryParams'
import { useUpdateVcm } from 'hooks/useUpdateVcm'
import { useWorkspace } from 'hooks/useWorkspace'
import {
  ALL_FILES_PATH,
  DOC_HISTORY_PATH,
  DOC_INSIGHTS_PATH,
  DOC_PATH,
  FOLDER_DOC_INSIGHTS_PATH,
  FOLDER_DOC_PATH,
  LOGIN_OR_SIGNUP_PATH,
} from 'paths'
import { useFinderRefresh } from 'screens/Finder/hooks/useFinderRefresh'
import { renderPrintPreview } from 'screens/PrintPreview/PrintPreview'
import { DocMode } from 'stores/DocStore.types'
import { useRootStore } from 'stores/RootStore'
import { AlmanacFile, ArticleView, ArticleViewRequest, HandbookItemType, RootState, UserRole } from 'types'
import { APP_ROOT_PATH, HELP_CENTER_WHAT_IS_ALMANAC_URL } from 'utils/constants'
import { navigateToExternalUrl } from 'utils/location'
import { getAlmanacUrlFor, getSearchString, redirectToSignup } from 'utils/urlUtils'
import { SwitchViewMenu } from './SwitchViewMenu'
import { DocMenuProps } from './Types'
import Styles from './DocMenu.module.scss'
import { useQueryClient } from 'react-query'
import { useCreateFavorite } from 'hooks/queries/favorites/useCreateFavorite'
import { useDeleteFavorite } from 'hooks/queries/favorites/useDeleteFavorite'
import { Item } from 'components/Menu/MenuItem'
import { useExportToZip } from 'components/ExportModal/hooks/useExportToZip'
import { Download } from 'blaze-ui/icons'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useCreateExportSync } from 'hooks/queries/useCreateExportSync'
import prntr from 'prntr'
import { PrintingModal } from 'components/DocMenu/PrintingModal'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ExportClient } from 'api/clients/ExportClient'
import { ExportFormatEnum, PdfPaperSize } from 'components/ExportModal/Types'
import { usePaywallGuard } from 'hooks/usePaywallGuard'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import cx from 'classnames'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { ChevronRight } from 'blaze-ui/icons/24'

const DocMenu = observer(
  ({ article, hiddenKeys, onAction, articleView, menuType = 'dropdown', onDeleteSuccess, ...props }: DocMenuProps) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const file = article.almanac_file
    const workspace = useWorkspace(file.workspace.id)
    const inViewMode = canViewIn(articleView)
    const canViewAs = useCanViewAs(file.workspace.id)
    const { focusView } = useQueryParams()
    const history = useHistory()
    const location = useLocation()
    const inFolder = useRouteMatch<{ folderSlug: string; doc?: string }>(FOLDER_DOC_PATH)
    const inDoc = useRouteMatch<{ slug: string }>(DOC_PATH)
    const { docStore, handbooks, files } = useRootStore()
    const isLoggedIn = useLoggedIn()
    const refreshFinder = useFinderRefresh()
    const openWindowSafely = useOpenWindowSafely()
    const { openModal, closeModal } = useModals()
    const { isChannelSocialPostable, isChannelPostable } = useIsChannelPostable()
    const isPrintPreviewV2Enabled = useFlipperFlag('print_preview_v2')
    const apiKeyFFActive = useFlipperFlag('api_keys')
    const { isDocumentationEnabled } = useDocumentationFeatures()
    const createFavorite = useCreateFavorite()
    const deleteFavorite = useDeleteFavorite()
    const queryClient = useQueryClient()
    const { exportToZip } = useExportToZip(article.almanac_file.id)
    const almanacFile = files.get(file.id)
    const { mutateAsync: createExport } = useCreateExportSync()
    const abortExport = useRef(new AbortController())
    const guardedOpenModal = usePaywallGuard(openModal)
    const { isMobile } = useResponsiveDesign()

    const { onItemCopied } = useOnItemCopied({
      title: file.title,
      folderSlug: last(file.ancestors)?.seo_string,
      handbooksStore: handbooks,
    })

    // State
    const favorite = useSelector((state: RootState) =>
      state.favorites.find(
        ({ favoritable_id, favoritable_type }) =>
          favoritable_id === file.id && favoritable_type === FavoritableType.Article,
      ),
    )
    const focusMode = useSelector((state: RootState) => !state.focusMode.focusModeDisabled)
    const { updateVcm } = useUpdateVcm(file.id, 'article')
    const isVersionControlOverridden = file?.is_version_controlled && !file.version_control_mode.value

    const getCanonicalUrl = () => {
      const pathname = generatePath(DOC_PATH, { slug: file.seo_string })
      const search = QueryString.stringify(
        { focusView },
        {
          filter: (_, value) => value ?? undefined,
          addQueryPrefix: true,
        },
      )
      return getAlmanacUrlFor(`${pathname}${search}`)
    }

    // Authorisation
    const ability = useAbility(AbilityContext)
    const authedArticle = {
      // This is a workaround until we redesign how we expose non-accessible workspaces.
      role: workspace?.role || UserRole.UNAUTHED,
      version_control_mode: file.is_version_controlled,
      permissions: file.permissions,
      __caslSubjectType__: 'Article',
    }

    const can: typeof ability.can = (action, subject) => {
      if (hiddenKeys?.has(action)) {
        return false
      }
      return ability.can(action, subject)
    }

    // Handlers
    const requestAccess = useArticleViewRequestChange({ articleKey: file.id })
    const changeArticleView = useArticleViewChange()

    const handleFavorite = () => {
      if (favorite) {
        deleteFavorite.mutate({ id: favorite.id, title: favorite.favoritable_title })
      } else {
        createFavorite.mutate({
          favoritableId: file.id,
          favoritableType: 'article',
          favoritableTitle: file.title,
        })
      }
    }

    const handleCopyLink = () => {
      copy(getCanonicalUrl())
      showToastMessage('Link copied')
    }

    const handleInsights = () => {
      // This is a good candidate for a hook that provides a generator for docs paths based on the current path.
      // ie.
      // const generateDocNestedPath = useDocNestedPathGenerator()
      // generatedDocNestedPath(DOC_INSIGHTS_PATH)
      history.push(
        inFolder
          ? generatePath(FOLDER_DOC_INSIGHTS_PATH, { ...inFolder.params, doc: file.seo_string })
          : generatePath(DOC_INSIGHTS_PATH, { ...inDoc?.params, slug: file.seo_string }),
      )
    }

    const handleHistory = () => {
      history.push(generatePath(DOC_HISTORY_PATH, { slug: file.seo_string }))
    }

    const handleCleanup = () => {
      docStore?.setDocMode(DocMode.Cleanup)
    }

    const handleFocusMode = () => {
      UserAppPreferencesService.setFocusModeEnabled(!focusMode)
    }

    const handleVersionControlMode = () => {
      updateVcm(!file.version_control_mode.value)
    }

    const loadParentFolder = async () => {
      const parent = last(file.ancestors)
      let parentFolder: AlmanacFile | undefined

      if (parent) {
        const response = await FolderClient.getFolder(parent.id)
        if (response.type === SUCCESS_RESPONSE_TYPE) {
          parentFolder = response.data.folder
        }
      }

      return parentFolder
    }

    const handleCopy = async () => {
      if (!isLoggedIn) {
        const copyPath = encodeURIComponent(`${location.pathname}?${getSearchString({ copyTemplate: 'true' })}`)
        // Clear auth cookie, if not user will receive wrong_scope error and will not be able to log in
        UserService.unsetAuthCookie()
        redirectToSignup(history, { whenDoneRedirectTo: copyPath })
        return
      }

      const currentFolder = await loadParentFolder()

      guardedOpenModal(CopyModal, {
        item: {
          id: file.id,
          title: file.title,
          header_emoji: file.emoji,
          linked_copies_count: 0,
        },
        folder: currentFolder,
        onItemCopied,
      })
    }

    const handleDelete = () => {
      const treeItem = values(handbooks.items).find(
        (item) => item.type === HandbookItemType.Document && item.data.document?.key === file.id,
      )

      openModal(DeleteModal, {
        files: [{ ...file, type: 'article' }],
        onRequest: () => {
          if (treeItem) {
            handbooks.removeItem(treeItem.id)
          }

          if (docStore?.articleId === file.id) {
            if (history.action === 'POP') {
              history.push(generatePath(ALL_FILES_PATH, { workspaceId: file.workspace.id }))
            } else {
              history.goBack()
            }
          }

          queryClient.invalidateQueries(['workspace', file.workspace.id, 'files'])
          queryClient.invalidateQueries(calendarKeys.all)
          refreshFinder()
          onDeleteSuccess?.()

          closeModal()
        },
      })
    }

    const handleExport = () => {
      openModal(
        ExportModal,
        {
          articleId: file.id,
        },
        { newStack: true },
      )
    }

    const handleDownload = () => {
      exportToZip()
    }

    const handleShare = () => {
      openModal(ShareSheet, {
        file: {
          id: file.id,
          type: 'article',
        },
      })
    }

    const handleArticlePublish = () => {
      if (almanacFile) {
        openModal(PublishDocModal, {
          file: almanacFile,
        })
      }
    }

    const handlePrint = async () => {
      if (isPrintPreviewV2Enabled) {
        abortExport.current = new AbortController()
        openModal(PrintingModal, {
          onCancel: () => {
            abortExport.current.abort()
            closeModal()
          },
        })
        try {
          const fileExport = await createExport({
            almanacFileId: file.id,
            exportFormat: ExportFormatEnum.Pdf,
            pageFormat: PdfPaperSize.Letter,
            signal: abortExport.current.signal,
          })
          if (!fileExport.file_url) {
            showErrorMessageToast('Error printing document')
            return
          }
          const response = await ExportClient.downloadExport(file.id, fileExport.id)
          const blob = await response.data.blob()
          prntr({ printable: URL.createObjectURL(blob), type: 'pdf' })
        } catch (error) {
          if (!(error instanceof DOMException) || error.name !== 'AbortError') {
            reportError(error)
            showErrorMessageToast('Error printing document')
          }
        } finally {
          closeModal()
        }
        return
      }
      renderPrintPreview({ articleKey: file.id })
    }

    const handleAction = async (action: Key) => {
      if (action === 'openInNewTab') {
        openWindowSafely(getCanonicalUrl())
      }

      if (action === 'requestCommentAccess') {
        requestAccess(ArticleViewRequest.Comment)
      }

      if (action === 'requestEditAccess') {
        requestAccess(ArticleViewRequest.Edit)
      }

      if (action === 'switchViewEditing') {
        changeArticleView(ArticleView.Editing)
      }

      if (action === 'switchViewCommenting') {
        changeArticleView(ArticleView.Commenting)
      }

      if (action === 'switchViewReading') {
        changeArticleView(ArticleView.Reading)
      }

      if (action === 'favorite') {
        handleFavorite()
      }

      if (action === 'copyLink') {
        handleCopyLink()
      }

      if (action === 'copy') {
        handleCopy()
      }

      if (action === 'readInsights') {
        handleInsights()
      }

      if (action === 'readHistory') {
        handleHistory()
      }

      if (action === 'cleanup') {
        handleCleanup()
      }

      if (action === 'toggleFocus') {
        handleFocusMode()
      }

      if (action === 'toggleVersionControl') {
        handleVersionControlMode()
      }

      if (action === 'move') {
        // NOOP: handled by the parent
      }

      if (action === 'login') {
        history.push(LOGIN_OR_SIGNUP_PATH)
      }

      if (action === 'whatIsAlmanac') {
        navigateToExternalUrl(HELP_CENTER_WHAT_IS_ALMANAC_URL)
      }

      if (action === 'whatIsBlaze') {
        navigateToExternalUrl(APP_ROOT_PATH)
      }

      if (action === 'delete') {
        handleDelete()
      }

      if (action === 'export') {
        handleExport()
      }

      if (action === 'download') {
        handleDownload()
      }

      if (action === 'print') {
        handlePrint()
      }

      if (action === 'publish') {
        handleArticlePublish()
      }

      if (action === 'rename') {
        // NOOP: handled by the parent
      }

      if (action === 'share') {
        handleShare()
      }

      onAction?.(action)
    }

    let disabledKeys = new Set(props.disabledKeys)
    if (isVersionControlOverridden) {
      disabledKeys.add('toggleVersionControl')
    }

    const Chevron = menuType === 'tray' ? ChevronRight : undefined
    const iconSize = isSystemRedesignEnabled ? 16 : 20
    const showAutoFocus = can('toggleFocus', authedArticle) && inViewMode(ArticleView.Editing) && articleView

    return (
      <Menu
        menuType={menuType}
        onAction={handleAction}
        disabledKeys={disabledKeys}
        aria-label="Document Menu"
        className={cx(Styles[menuType], { [Styles.showAutoFocus]: !!showAutoFocus })}
        itemClassName={Styles.menuItem}
      >
        <Section>
          {
            [
              !hiddenKeys?.has('openInNewTab') && (
                <Item key="openInNewTab" textValue="Open in new tab" frontIcon={ArrowOut}>
                  Open in new tab
                </Item>
              ),
              canViewAs(UserRole.UNAUTHED) && (
                <Item key="login" textValue="Log In / Sign Up" frontIcon={Login}>
                  Log In / Sign Up
                </Item>
              ),
              canViewAs(UserRole.UNAUTHED) && (
                <Item key="whatIsBlaze" textValue="What is Blaze?" frontIcon={BlazeAI}>
                  What is Blaze?
                </Item>
              ),
              can('move', authedArticle) && inViewMode(ArticleView.Editing) && (
                <Item key="move" textValue="Move" frontIcon={Move} endIcon={Chevron}>
                  Move to
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              can('requestCommentAccess', authedArticle) &&
                inViewMode(ArticleView.Reading) &&
                canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                  <Item key="requestCommentAccess" textValue="Request Comment Access" frontIcon={Comment}>
                    Request Comment Access
                  </Item>
                ),
              can('requestEditAccess', authedArticle) &&
                inViewMode(ArticleView.Reading, ArticleView.Commenting) &&
                canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                  <Item key="requestEditAccess" textValue="Request Edit Access" frontIcon={Edit}>
                    Request Edit Access
                  </Item>
                ),
              isDocumentationEnabled &&
                can('toggleVersionControl', authedArticle) &&
                inViewMode(ArticleView.Commenting, ArticleView.Editing) && (
                  <Item key="toggleVersionControl" textValue="Version Control Mode" frontIcon={Merge}>
                    <Tooltip
                      placement="left"
                      label={
                        file.workspace?.version_control_mode.value
                          ? 'Version Control Mode is ON for the workspace'
                          : 'Version Control Mode is ON for the folder this file is in'
                      }
                      hide={!isVersionControlOverridden}
                    >
                      <div className={Styles.tooltipOverlay} />
                    </Tooltip>
                    <span className={Styles.label}>Version Control Mode</span>
                    <VisualSwitch active={file.is_version_controlled} variant="sm" />
                  </Item>
                ),
              can('switchView', authedArticle) && articleView && (
                <Item key="switchView" textValue="Editing">
                  {articleView === ArticleView.Editing && (
                    <>
                      <Edit size={iconSize} /> <span className={Styles.label}>Editing</span>
                    </>
                  )}
                  {articleView === ArticleView.Commenting && (
                    <>
                      <Comment size={iconSize} /> <span className={Styles.label}>Commenting</span>
                    </>
                  )}
                  {articleView === ArticleView.Reading && (
                    <>
                      <ReadReceipt size={iconSize} /> <span className={Styles.label}>Reading</span>
                    </>
                  )}
                  {
                    // @ts-ignore
                    () => (
                      <SwitchViewMenu
                        articleView={articleView}
                        onAction={(newView) => handleAction(`switchView${newView}`)}
                      />
                    )
                  }
                </Item>
              ),
              can('favorite', authedArticle) && (
                <Item
                  key="favorite"
                  textValue={favorite ? 'Remove from Favorites' : 'Add to Favorites'}
                  frontIcon={favorite ? Unfavorite : Favorite}
                >
                  {favorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </Item>
              ),

              can('share', authedArticle) && (
                <Item key="share" textValue="Share" frontIcon={Share}>
                  Share
                </Item>
              ),
              can('copyLink', authedArticle) && (
                <Item key="copyLink" textValue="Copy Link" frontIcon={Link}>
                  Copy Link
                </Item>
              ),
              can('copy', authedArticle) && (
                <Item key="copy" textValue="Make a Copy" frontIcon={Copy}>
                  Make a Copy
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>

        <Section>
          {
            [
              can('readHistory', authedArticle) && (
                <Item key="readHistory" textValue="Doc History" frontIcon={History}>
                  Doc History
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>

        <Section>
          {
            [
              can('print', authedArticle) && !isMobile && (
                <Item key="print" textValue="Print" frontIcon={Print} endIcon={Chevron}>
                  Print
                </Item>
              ),
              almanacFile &&
                isChannelPostable(almanacFile.channel as ChannelType) &&
                can('publish', authedArticle) &&
                apiKeyFFActive && (
                  <Item key="publish" textValue="Publish" frontIcon={ArrowOut} endIcon={Chevron}>
                    Publish
                  </Item>
                ),
              can('export', authedArticle) && (
                <Item key="export" textValue="Export" frontIcon={Export} endIcon={Chevron}>
                  Export
                </Item>
              ),
              almanacFile &&
                isChannelSocialPostable(almanacFile.channel as ChannelType) &&
                can('export', authedArticle) && (
                  <Item key="download" textValue="Download all Contents" frontIcon={Download} endIcon={Chevron}>
                    Download all Contents
                  </Item>
                ),
              can('rename', authedArticle) && inViewMode(ArticleView.Editing) && (
                <Item key="rename" textValue="Rename" frontIcon={Edit}>
                  Rename
                </Item>
              ),
              can('delete', authedArticle) && inViewMode(ArticleView.Editing) && (
                <Item key="delete" textValue="Delete" frontIcon={Delete}>
                  Delete
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>

        <Section>
          {
            [
              showAutoFocus && (
                <Item key="toggleFocus" textValue="Auto Focus Mode" frontIcon={Focus}>
                  <span className={Styles.label}>Auto Focus Mode</span>
                  <VisualSwitch active={focusMode} variant="sm" />
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
      </Menu>
    )
  },
)

export { DocMenu }
